import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Tag, 
  Progress, 
  Card, 
  Row, 
  Col,
  Tooltip,
  Popconfirm,
  message,
  Divider,
  Badge,
  Tabs,
  Timeline,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import moment from 'moment';
import {
  getPlanesTrabajo,
  getPlanTrabajoById,
  createPlanTrabajo,
  updatePlanTrabajo,
  deletePlanTrabajo,
  getEstadisticasPlanTrabajo,
  updateActividadEstado,
  getUsuarios,
  getActividades,
  getSecciones,
  getLotes
} from '../services/api';
import NavigationBar from '../components/layout/Navbar';
import ExportPlanesToExcel from '../components/export/ExportPlanesToExcel';
import '../assets/styles/DatabaseStyles.css';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { TabPane } = Tabs;

const PlanTrabajo = () => {
  // Estados principales
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form] = Form.useForm();
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados para datos de soporte
  const [usuarios, setUsuarios] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  
  // Estados para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Estados para actividades del plan
  const [planActividades, setPlanActividades] = useState([]);
  const [planLotes, setPlanLotes] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchPlanes();
  }, [pagination.current, pagination.pageSize, filtros]);

  const fetchInitialData = async () => {
    try {
      const [usuariosData, actividadesData, seccionesData, estadisticasData] = await Promise.all([
        getUsuarios(),
        getActividades(),
        getSecciones(),
        getEstadisticasPlanTrabajo()
      ]);
      
      setUsuarios(usuariosData);
      setActividades(actividadesData);
      setSecciones(seccionesData);
      setEstadisticas(estadisticasData);
    } catch (error) {
      message.error('Error al cargar datos iniciales');
    }
  };

  const fetchPlanes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filtros
      };
      
      const response = await getPlanesTrabajo(params);
      setPlanes(response.planes);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('Error al cargar planes de trabajo');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      const planData = {
        ...values,
        fecha_inicio: values.fechas[0].format('YYYY-MM-DD'),
        fecha_fin: values.fechas[1].format('YYYY-MM-DD'),
        actividades: planActividades,
        lotes: planLotes
      };
      delete planData.fechas;

      if (editingPlan) {
        await updatePlanTrabajo(editingPlan.id, planData);
        message.success('Plan de trabajo actualizado exitosamente');
      } else {
        await createPlanTrabajo(planData);
        message.success('Plan de trabajo creado exitosamente');
      }
      
      setModalVisible(false);
      setEditingPlan(null);
      setPlanActividades([]);
      setPlanLotes([]);
      form.resetFields();
      fetchPlanes();
      fetchInitialData(); // Actualizar estadísticas
    } catch (error) {
      message.error(editingPlan ? 'Error al actualizar plan' : 'Error al crear plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setPlanActividades(plan.planActividadesDetalle || []);
    setPlanLotes(plan.planLotesDetalle || []);
    
    form.setFieldsValue({
      titulo: plan.titulo,
      descripcion: plan.descripcion,
      fechas: [moment(plan.fecha_inicio), moment(plan.fecha_fin)],
      prioridad: plan.prioridad,
      usuario_responsable_id: plan.usuario_responsable_id,
      observaciones: plan.observaciones
    });
    
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deletePlanTrabajo(id);
      message.success('Plan de trabajo eliminado exitosamente');
      fetchPlanes();
      fetchInitialData(); // Actualizar estadísticas
    } catch (error) {
      message.error('Error al eliminar plan de trabajo');
    }
  };

  const handleViewDetail = async (plan) => {
    try {
      const detailedPlan = await getPlanTrabajoById(plan.id);
      setSelectedPlan(detailedPlan);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('Error al cargar detalles del plan');
    }
  };

  const handleUpdateActividadEstado = async (planId, actividadId, nuevoEstado) => {
    try {
      await updateActividadEstado(planId, actividadId, {
        estado_actividad: nuevoEstado
      });
      message.success('Estado de actividad actualizado');
      
      // Actualizar el plan seleccionado si está abierto
      if (selectedPlan && selectedPlan.id === planId) {
        const updatedPlan = await getPlanTrabajoById(planId);
        setSelectedPlan(updatedPlan);
      }
      
      fetchPlanes();
    } catch (error) {
      message.error('Error al actualizar estado de actividad');
    }
  };

  const addActividad = () => {
    setPlanActividades([...planActividades, {
      actividad_id: '',
      subactividad_id: '',
      fecha_planificada: '',
      horas_estimadas: '',
      observaciones: '',
      orden: planActividades.length + 1
    }]);
  };

  const removeActividad = (index) => {
    const newActividades = planActividades.filter((_, i) => i !== index);
    setPlanActividades(newActividades);
  };

  const updateActividad = (index, field, value) => {
    const newActividades = [...planActividades];
    newActividades[index] = { ...newActividades[index], [field]: value };
    setPlanActividades(newActividades);
  };

  const addLote = () => {
    setPlanLotes([...planLotes, {
      lote_id: '',
      area_planificada: '',
      observaciones: ''
    }]);
  };

  const removeLote = (index) => {
    const newLotes = planLotes.filter((_, i) => i !== index);
    setPlanLotes(newLotes);
  };

  const updateLote = (index, field, value) => {
    const newLotes = [...planLotes];
    newLotes[index] = { ...newLotes[index], [field]: value };
    setPlanLotes(newLotes);
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'planificado': 'blue',
      'en_progreso': 'orange',
      'completado': 'green',
      'cancelado': 'red',
      'pausado': 'gray'
    };
    return colors[estado] || 'default';
  };

  const getPrioridadColor = (prioridad) => {
    const colors = {
      'baja': 'green',
      'media': 'orange',
      'alta': 'red',
      'urgente': 'purple'
    };
    return colors[prioridad] || 'default';
  };

  const getEstadoIcon = (estado) => {
    const icons = {
      'planificado': <ClockCircleOutlined />,
      'en_progreso': <ExclamationCircleOutlined />,
      'completado': <CheckCircleOutlined />,
      'cancelado': <StopOutlined />,
      'pausado': <PauseCircleOutlined />
    };
    return icons[estado] || <ClockCircleOutlined />;
  };

  const columns = [
    {
      title: 'Título',
      dataIndex: 'titulo',
      key: 'titulo',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {record.id}
          </div>
        </div>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 120,
      render: (estado) => (
        <Tag color={getEstadoColor(estado)} icon={getEstadoIcon(estado)}>
          {estado.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Planificado', value: 'planificado' },
        { text: 'En Progreso', value: 'en_progreso' },
        { text: 'Completado', value: 'completado' },
        { text: 'Cancelado', value: 'cancelado' },
        { text: 'Pausado', value: 'pausado' }
      ]
    },
    {
      title: 'Prioridad',
      dataIndex: 'prioridad',
      key: 'prioridad',
      width: 100,
      render: (prioridad) => (
        <Tag color={getPrioridadColor(prioridad)}>
          {prioridad.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Baja', value: 'baja' },
        { text: 'Media', value: 'media' },
        { text: 'Alta', value: 'alta' },
        { text: 'Urgente', value: 'urgente' }
      ]
    },
    {
      title: 'Progreso',
      dataIndex: 'progreso_porcentaje',
      key: 'progreso',
      width: 120,
      render: (progreso) => (
        <Progress 
          percent={parseFloat(progreso)} 
          size="small" 
          status={progreso === 100 ? 'success' : 'active'}
        />
      )
    },
    {
      title: 'Responsable',
      dataIndex: ['usuarioResponsable', 'nombre'],
      key: 'responsable',
      width: 150,
      render: (_, record) => (
        <div>
          <UserOutlined style={{ marginRight: 5 }} />
          {record.usuarioResponsable?.nombre} {record.usuarioResponsable?.apellido}
        </div>
      )
    },
    {
      title: 'Fechas',
      key: 'fechas',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <CalendarOutlined style={{ marginRight: 5 }} />
            Inicio: {moment(record.fecha_inicio).format('DD/MM/YYYY')}
          </div>
          <div style={{ fontSize: '12px' }}>
            <CalendarOutlined style={{ marginRight: 5 }} />
            Fin: {moment(record.fecha_fin).format('DD/MM/YYYY')}
          </div>
        </div>
      )
    },
    {
      title: 'Actividades',
      dataIndex: 'planActividadesDetalle',
      key: 'actividades',
      width: 100,
      render: (actividades) => (
        <Badge count={actividades?.length || 0} showZero />
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Popconfirm
              title="¿Estás seguro de eliminar este plan?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  const renderEstadisticas = () => (
    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col span={6}>
        <Card>
          <Statistic
            title="Total de Planes"
            value={estadisticas.totalPlanes || 0}
            prefix={<BarChartOutlined />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="Progreso Promedio"
            value={estadisticas.progresoPromedio || 0}
            suffix="%"
            prefix={<CheckCircleOutlined />}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="En Progreso"
            value={estadisticas.estadisticasEstado?.find(e => e.estado === 'en_progreso')?.cantidad || 0}
            prefix={<ExclamationCircleOutlined />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card>
          <Statistic
            title="Completados"
            value={estadisticas.estadisticasEstado?.find(e => e.estado === 'completado')?.cantidad || 0}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
    </Row>
  );

  return (
    <div>
      <NavigationBar />
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1>Plan de Trabajo</h1>
          <p>Gestiona y planifica las actividades de trabajo</p>
        </div>

        {renderEstadisticas()}

        <Card>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingPlan(null);
                  setPlanActividades([]);
                  setPlanLotes([]);
                  form.resetFields();
                  setModalVisible(true);
                }}
              >
                Nuevo Plan
              </Button>
              <Button 
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtros
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchPlanes();
                  fetchInitialData();
                }}
              >
                Actualizar
              </Button>
              <ExportPlanesToExcel 
                planes={planes}
                usuarios={usuarios}
                actividades={actividades}
                filtros={filtros}
              />
            </Space>
          </div>

          {showFilters && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
              <Form layout="inline">
                <Form.Item label="Estado">
                  <Select
                    placeholder="Seleccionar estado"
                    style={{ width: 150 }}
                    allowClear
                    onChange={(value) => setFiltros(prev => ({ ...prev, estado: value }))}
                  >
                    <Option value="planificado">Planificado</Option>
                    <Option value="en_progreso">En Progreso</Option>
                    <Option value="completado">Completado</Option>
                    <Option value="cancelado">Cancelado</Option>
                    <Option value="pausado">Pausado</Option>
                  </Select>
                </Form.Item>
                <Form.Item label="Prioridad">
                  <Select
                    placeholder="Seleccionar prioridad"
                    style={{ width: 120 }}
                    allowClear
                    onChange={(value) => setFiltros(prev => ({ ...prev, prioridad: value }))}
                  >
                    <Option value="baja">Baja</Option>
                    <Option value="media">Media</Option>
                    <Option value="alta">Alta</Option>
                    <Option value="urgente">Urgente</Option>
                  </Select>
                </Form.Item>
                <Form.Item label="Responsable">
                  <Select
                    placeholder="Seleccionar responsable"
                    style={{ width: 200 }}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => setFiltros(prev => ({ ...prev, usuario_responsable_id: value }))}
                  >
                    {usuarios.map(usuario => (
                      <Option key={usuario.id} value={usuario.id}>
                        {usuario.nombre} {usuario.apellido}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Fechas">
                  <RangePicker
                    onChange={(dates) => {
                      if (dates) {
                        setFiltros(prev => ({
                          ...prev,
                          fecha_inicio: dates[0].format('YYYY-MM-DD'),
                          fecha_fin: dates[1].format('YYYY-MM-DD')
                        }));
                      } else {
                        setFiltros(prev => {
                          const newFiltros = { ...prev };
                          delete newFiltros.fecha_inicio;
                          delete newFiltros.fecha_fin;
                          return newFiltros;
                        });
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item>
                  <Button onClick={() => setFiltros({})}>
                    Limpiar Filtros
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          <Table
            columns={columns}
            dataSource={planes}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} planes`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize
                }));
              }
            }}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Modal para crear/editar plan */}
        <Modal
          title={editingPlan ? 'Editar Plan de Trabajo' : 'Nuevo Plan de Trabajo'}
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingPlan(null);
            setPlanActividades([]);
            setPlanLotes([]);
            form.resetFields();
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateOrUpdate}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="titulo"
                  label="Título"
                  rules={[{ required: true, message: 'Por favor ingresa el título' }]}
                >
                  <Input placeholder="Título del plan" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="usuario_responsable_id"
                  label="Responsable"
                  rules={[{ required: true, message: 'Por favor selecciona un responsable' }]}
                >
                  <Select placeholder="Seleccionar responsable" showSearch>
                    {usuarios.map(usuario => (
                      <Option key={usuario.id} value={usuario.id}>
                        {usuario.nombre} {usuario.apellido}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="fechas"
                  label="Fechas de Inicio y Fin"
                  rules={[{ required: true, message: 'Por favor selecciona las fechas' }]}
                >
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="prioridad"
                  label="Prioridad"
                  rules={[{ required: true, message: 'Por favor selecciona la prioridad' }]}
                >
                  <Select placeholder="Seleccionar prioridad">
                    <Option value="baja">Baja</Option>
                    <Option value="media">Media</Option>
                    <Option value="alta">Alta</Option>
                    <Option value="urgente">Urgente</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="descripcion"
              label="Descripción"
            >
              <TextArea rows={3} placeholder="Descripción del plan" />
            </Form.Item>

            <Form.Item
              name="observaciones"
              label="Observaciones"
            >
              <TextArea rows={2} placeholder="Observaciones adicionales" />
            </Form.Item>

            <Divider>Actividades del Plan</Divider>
            
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" onClick={addActividad} block>
                <PlusOutlined /> Agregar Actividad
              </Button>
            </div>

            {planActividades.map((actividad, index) => (
              <Card key={index} size="small" style={{ marginBottom: 8 }}>
                <Row gutter={8} align="middle">
                  <Col span={5}>
                    <Select
                      placeholder="Actividad"
                      value={actividad.actividad_id}
                      onChange={(value) => updateActividad(index, 'actividad_id', value)}
                      style={{ width: '100%' }}
                    >
                      {actividades.map(act => (
                        <Option key={act.id} value={act.id}>
                          {act.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={4}>
                    <DatePicker
                      placeholder="Fecha"
                      value={actividad.fecha_planificada ? moment(actividad.fecha_planificada) : null}
                      onChange={(date) => updateActividad(index, 'fecha_planificada', date?.format('YYYY-MM-DD'))}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <Input
                      placeholder="Horas"
                      type="number"
                      value={actividad.horas_estimadas}
                      onChange={(e) => updateActividad(index, 'horas_estimadas', e.target.value)}
                    />
                  </Col>
                  <Col span={6}>
                    <Input
                      placeholder="Observaciones"
                      value={actividad.observaciones}
                      onChange={(e) => updateActividad(index, 'observaciones', e.target.value)}
                    />
                  </Col>
                  <Col span={2}>
                    <Input
                      placeholder="Orden"
                      type="number"
                      value={actividad.orden}
                      onChange={(e) => updateActividad(index, 'orden', e.target.value)}
                    />
                  </Col>
                  <Col span={2}>
                    <Button 
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />}
                      onClick={() => removeActividad(index)}
                    />
                  </Col>
                </Row>
              </Card>
            ))}

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setModalVisible(false);
                  setEditingPlan(null);
                  setPlanActividades([]);
                  setPlanLotes([]);
                  form.resetFields();
                }}>
                  Cancelar
                </Button>
                <Button type="primary" htmlType="submit">
                  {editingPlan ? 'Actualizar' : 'Crear'} Plan
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* Modal de detalles del plan */}
        <Modal
          title={`Detalles del Plan: ${selectedPlan?.titulo}`}
          visible={detailModalVisible}
          onCancel={() => {
            setDetailModalVisible(false);
            setSelectedPlan(null);
          }}
          footer={null}
          width={900}
        >
          {selectedPlan && (
            <Tabs defaultActiveKey="1">
              <TabPane tab="Información General" key="1">
                <Row gutter={16}>
                  <Col span={12}>
                    <Card title="Datos del Plan" size="small">
                      <p><strong>Estado:</strong> <Tag color={getEstadoColor(selectedPlan.estado)}>{selectedPlan.estado}</Tag></p>
                      <p><strong>Prioridad:</strong> <Tag color={getPrioridadColor(selectedPlan.prioridad)}>{selectedPlan.prioridad}</Tag></p>
                      <p><strong>Progreso:</strong> <Progress percent={parseFloat(selectedPlan.progreso_porcentaje)} /></p>
                      <p><strong>Descripción:</strong> {selectedPlan.descripcion || 'Sin descripción'}</p>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Fechas y Responsables" size="small">
                      <p><strong>Fecha Inicio:</strong> {moment(selectedPlan.fecha_inicio).format('DD/MM/YYYY')}</p>
                      <p><strong>Fecha Fin:</strong> {moment(selectedPlan.fecha_fin).format('DD/MM/YYYY')}</p>
                      <p><strong>Responsable:</strong> {selectedPlan.usuarioResponsable?.nombre} {selectedPlan.usuarioResponsable?.apellido}</p>
                      <p><strong>Creado por:</strong> {selectedPlan.usuarioCreador?.nombre} {selectedPlan.usuarioCreador?.apellido}</p>
                      <p><strong>Fecha Creación:</strong> {moment(selectedPlan.fecha_creacion).format('DD/MM/YYYY HH:mm')}</p>
                    </Card>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Actividades" key="2">
                <div style={{ marginBottom: 16 }}>
                  <h4>Actividades del Plan ({selectedPlan.planActividadesDetalle?.length || 0})</h4>
                </div>
                <Timeline>
                  {selectedPlan.planActividadesDetalle?.map((actividad, index) => (
                    <Timeline.Item
                      key={index}
                      color={actividad.estado_actividad === 'completada' ? 'green' : 
                             actividad.estado_actividad === 'en_progreso' ? 'blue' : 'gray'}
                      dot={getEstadoIcon(actividad.estado_actividad)}
                    >
                      <div>
                        <h5>{actividad.actividad?.nombre}</h5>
                        {actividad.subactividad && <p>Subactividad: {actividad.subactividad.nombre}</p>}
                        <p>Fecha Planificada: {moment(actividad.fecha_planificada).format('DD/MM/YYYY')}</p>
                        {actividad.horas_estimadas && <p>Horas Estimadas: {actividad.horas_estimadas}</p>}
                        <p>Estado: <Tag color={getEstadoColor(actividad.estado_actividad)}>{actividad.estado_actividad}</Tag></p>
                        {actividad.observaciones_actividad && <p>Observaciones: {actividad.observaciones_actividad}</p>}
                        
                        <Space style={{ marginTop: 8 }}>
                          <Button 
                            size="small" 
                            type={actividad.estado_actividad === 'pendiente' ? 'primary' : 'default'}
                            onClick={() => handleUpdateActividadEstado(selectedPlan.id, actividad.id, 'en_progreso')}
                            disabled={actividad.estado_actividad === 'completada'}
                          >
                            Iniciar
                          </Button>
                          <Button 
                            size="small" 
                            type={actividad.estado_actividad === 'completada' ? 'primary' : 'default'}
                            onClick={() => handleUpdateActividadEstado(selectedPlan.id, actividad.id, 'completada')}
                            disabled={actividad.estado_actividad === 'completada'}
                          >
                            Completar
                          </Button>
                        </Space>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </TabPane>
              
              <TabPane tab="Lotes" key="3">
                <div style={{ marginBottom: 16 }}>
                  <h4>Lotes Asignados ({selectedPlan.planLotesDetalle?.length || 0})</h4>
                </div>
                {selectedPlan.planLotesDetalle?.map((loteDetalle, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <Row>
                      <Col span={12}>
                        <p><strong>Lote:</strong> {loteDetalle.lote?.nombre}</p>
                        <p><strong>Sección:</strong> {loteDetalle.lote?.seccion?.nombre}</p>
                      </Col>
                      <Col span={12}>
                        {loteDetalle.area_planificada && <p><strong>Área Planificada:</strong> {loteDetalle.area_planificada} ha</p>}
                        {loteDetalle.observaciones_lote && <p><strong>Observaciones:</strong> {loteDetalle.observaciones_lote}</p>}
                      </Col>
                    </Row>
                  </Card>
                ))}
              </TabPane>
            </Tabs>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default PlanTrabajo;
